/**
 * Which browser-storage keys die with the signed-in identity, and which
 * deliberately outlive it.
 *
 * localStorage is shared by every person who ever signs in on the machine, and
 * two of the keys below leave the browser as REQUEST HEADERS: the axios
 * interceptor reads them outside React (shared/api/client.ts), so they act on
 * the server for whoever is signed in NEXT, not for whoever wrote them. That is
 * the bug this module exists for — an admin who logged out mid-impersonation
 * left `wz.impersonate` armed, and the next person to sign in on a shared
 * machine silently acted as the impersonated user.
 *
 * The durable keys are enumerated here too, with reasons, because the tempting
 * future edit is a `localStorage.clear()` in logout — and that would break
 * every invite claim in flight. AcceptInvitationPage's "use a different
 * account" button stashes the invitation token and THEN calls logout(),
 * counting on it to survive both the sign-out and the Auth0 round-trip after
 * it (see shared/storage/pendingAction.ts for why those live in localStorage).
 *
 * Removal also fires `storage` in the app's other tabs, so an impersonation
 * banner open elsewhere drops itself rather than going stale.
 */

/** ImpersonationContext's STORAGE_KEY. Becomes X-Impersonate-User-Id. */
export const IMPERSONATION_KEY = 'wz.impersonate';

/** ActiveOrgContext's STORAGE_KEY. Becomes X-Active-Org-Id. */
export const ACTIVE_CONTEXT_KEY = 'wz.activeContext';

/** ActiveOrgContext's PLATFORM_VIEW_KEY — which platform section the sidebar
 *  surfaces for a user who is both system admin and instructor. */
export const PLATFORM_VIEW_KEY = 'wz.platformView';

/**
 * The one-shot login_hint switchToAccount leaves for the anonymous reload.
 *
 * Cleared on a plain logout because an abandoned account switch would
 * otherwise bounce the next visitor straight into an Auth0 login prefilled
 * with the previous user's address. switchToAccount writes it AFTER clearing
 * for exactly this reason.
 */
export const SIGNIN_HINT_KEY = 'wz_signin_hint';

export const SESSION_SCOPED_KEYS: readonly string[] = [
  IMPERSONATION_KEY,
  ACTIVE_CONTEXT_KEY,
  PLATFORM_VIEW_KEY,
  SIGNIN_HINT_KEY,
];

/**
 * Keys logout must NOT touch.
 *
 * Pending actions (`wicklander_pending_invitation`,
 * `wz.pendingInviteLinkToken`, `wz.pendingSelfEnrollCourseSlug`,
 * `pending_partner_slug`) are queued *before* an auth redirect precisely so
 * they survive it, and each already carries its own 24h TTL.
 *
 * Post-auth navigation (`wz.postAuthReturnTo`, `wz.welcomeReturn`) is the same
 * story, and both are re-validated against an allowlist on read, so a stale
 * value can only ever mean a wrong landing page — never a privilege.
 *
 * Analytics keys are visitor-scoped, not account-scoped. Clearing `wz_consent`
 * would re-prompt the cookie banner after every logout and discard a recorded
 * privacy decision; `wz_attribution` is first-touch by design and its own
 * module owns erasure (clearFirstTouch, on consent withdrawal).
 * `wz_signup_sent:<userId>` is namespaced per user id so it cannot cross
 * identities, and `wz_pending_purchase` is per-tab and read-once.
 *
 * `wz.stubUser` belongs to StubAuthProvider, the dev-only Auth0 bypass, which
 * never renders alongside this provider and clears the key in its own logout.
 */
export const DURABLE_KEYS: readonly string[] = [
  'wicklander_pending_invitation',
  'wz.pendingInviteLinkToken',
  'wz.pendingSelfEnrollCourseSlug',
  'pending_partner_slug',
  'wz.postAuthReturnTo',
  'wz.welcomeReturn',
  'wz_consent',
  'wz_attribution',
  'wz_attribution_synced',
  'wz_pending_touch',
  'wz_pending_purchase',
  'wz.stubUser',
];

/**
 * Drop everything scoped to the identity that is going away. Safe to call more
 * than once, and safe when storage is unavailable (private browsing throws on
 * access, and there is nothing to clear in that case anyway).
 */
export function clearSessionScopedStorage(): void {
  for (const key of SESSION_SCOPED_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}
